import React, { useMemo } from 'react';

import Cell from 'components/V5Gadget/Matrix/Cell';
import useValueFormatters from 'hooks/useValueFormatters';
import { FleetopsGridCellRendererParams } from 'types/agGrid';
import computeScoreColor from 'components/DriverAX/utils/bonusScoreColourUtils';

import GridCellErrorBoundary from './GridCellErrorBoundary';

export const ColourCodedNumberCell = ({
  elasticDocument,
  dataset,
  field,
  colourConfig,
}: {
  elasticDocument: ElasticDocument;
  dataset: string;
  field: string;
  colourConfig?: FleetOps.BonusScoreColourConfig;
}) => {
  const { formatField } = useValueFormatters();

  const formattedValue = useMemo(
    () => formatField({ field, value: elasticDocument[field], dataset }),
    [dataset, elasticDocument, field, formatField],
  );

  const color = useMemo(
    () =>
      colourConfig
        ? computeScoreColor({ value: elasticDocument[field], colourConfig })
        : undefined,
    [colourConfig, elasticDocument, field],
  );

  return (
    <Cell rightAlign>
      <span style={{ color }}>{formattedValue}</span>
    </Cell>
  );
};

const ColourCodedNumberCellProvider = (
  params: FleetopsGridCellRendererParams,
) => {
  const elasticDocument = useMemo(
    () => params.node.data as ElasticDocument,
    [params],
  );

  const field = useMemo(() => {
    const { colDef } = params;
    if (!colDef || !colDef.field) {
      return undefined;
    }
    return colDef.field;
  }, [params]);

  const colourConfig = params.node.rowPinned
    ? undefined
    : params.colDef.cellRendererParams.fieldView?.bonusScoreColourConfig;

  const dataset = params.colDef.cellRendererParams.fieldView?.dataset ?? '';

  if (!field) {
    return null;
  }

  return (
    <ColourCodedNumberCell
      elasticDocument={elasticDocument}
      dataset={dataset}
      field={field}
      colourConfig={colourConfig}
    />
  );
};

const Gate = (params: FleetopsGridCellRendererParams) => (
  <GridCellErrorBoundary
    params={params}
    childComponent={<ColourCodedNumberCellProvider {...params} />}
  />
);

export default Gate;
